import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { ShoppingBagIcon, ExclamationTriangleIcon, TruckIcon, MagnifyingGlassIcon, ChatBubbleLeftRightIcon, TrashIcon, EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { fetchAllOrders, fetchProducts, updateOrderStatus, deleteProduct, fetchMessages, updateMessageStatus, deleteMessage, replyToMessage } from './index';
import { Product } from '../types';

interface OrderData {
  id: string;
  customer: {
    name: string;
    email: string;
  };
  items: string;
  total: number;
  status: string;
  date: string;
}

interface MessageData {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  date: string;
  status: 'New' | 'Read' | 'Replied'; // enum
}

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);

  const [replyMessage, setReplyMessage] = useState('');
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [loading, setLoading] = useState(true);

  // View State
  const [activeView, setActiveView] = useState<'orders' | 'inventory' | 'messages'>('orders');
  const [orderFilter, setOrderFilter] = useState<'all' | 'pending' | 'dispatched'>('all');
  const [inventoryFilter, setInventoryFilter] = useState<'all' | 'low'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Load Orders and Products
        const [ordersRes, productsRes] = await Promise.all([
          fetchAllOrders(),
          fetchProducts(),
        ]);
        setOrders(ordersRes.data.map((o: any) => ({ ...o, id: o.id || o._id })));
        setProducts(productsRes.data.map((p: any) => ({ ...p, id: p._id })));

        // Load Messages (Try API, fallback to LocalStorage)
        try {
          const messagesRes = await fetchMessages();
          const apiMessages = messagesRes.data.map((m: any) => ({ ...m, id: m._id || m.id }));
          const localMessages = JSON.parse(localStorage.getItem('kano_fresh_messages') || '[]');
          setMessages([...apiMessages, ...localMessages]);
        } catch (msgError) {
          console.warn("Messages API failed, loading local messages only");
          const localMessages = JSON.parse(localStorage.getItem('kano_fresh_messages') || '[]');
          setMessages(localMessages);
        }
      } catch (error) {
        console.error("Failed to load dashboard data", error);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Derived Data
  const lowStockItems = products.filter(p => (p.stock || 0) < 10);
  const pendingOrdersCount = orders.filter(o => o.status === 'Pending' || o.status === 'Processing' || o.status === 'Pending Verification').length;
  const dispatchedTodayCount = orders.filter(o => o.status === 'Dispatched').length;
  const newMessagesCount = messages.filter(m => m.status === 'New').length;

  // Filter Logic
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = 
      orderFilter === 'all' ? true :
      orderFilter === 'pending' ? (order.status === 'Pending' || order.status === 'Processing' || order.status === 'Pending Verification') :
      orderFilter === 'dispatched' ? order.status === 'Dispatched' : true;
    return matchesSearch && matchesStatus;
  });

  const filteredProducts = inventoryFilter === 'low' ? lowStockItems : products;

  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      // Optimistic update
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (error) {
      console.error("Failed to update status", error);
      alert("Failed to update order status");
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        await deleteProduct(id);
        setProducts(products.filter(p => p.id !== id));
        alert('Product deleted successfully');
      } catch (error) {
        console.error("Failed to delete product", error);
        alert("Failed to delete product. Ensure you have Super Admin privileges.");
      }
    }
  };

  const handleDeleteMessage = async (id: string) => {
    if (window.confirm('Delete this message?')) {
      try {
        await deleteMessage(id);
      } catch (error) {
        // Fallback for local messages
        const localMessages = JSON.parse(localStorage.getItem('kano_fresh_messages') || '[]');
        const updated = localMessages.filter((m: any) => m.id !== id);
        localStorage.setItem('kano_fresh_messages', JSON.stringify(updated));
      }
      setMessages(messages.filter(m => m.id !== id));
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await updateMessageStatus(id, 'Read');
    } catch (error) {
      // Fallback for local messages
      const localMessages = JSON.parse(localStorage.getItem('kano_fresh_messages') || '[]');
      const updated = localMessages.map((m: any) => m.id === id ? { ...m, status: 'Read' } : m);
      localStorage.setItem('kano_fresh_messages', JSON.stringify(updated));
    }
    setMessages(messages.map(m => m.id === id ? { ...m, status: 'Read' as const } : m));
  };

  const handleSendMessage = async (id: string) => {
    try {
      await replyToMessage(id, replyMessage);
      setMessages(messages.map(m => m.id === id ? { ...m, status: 'Replied' as const } : m));
      setReplyMessage('');
      alert('Reply sent successfully!');
    } catch (error) {
      console.error("Failed to send reply", error);
      alert("Failed to send reply");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold text-gray-900">Store Management</h1>
            <p className="mt-1 text-sm text-gray-500">Overview of your store's performance and inventory.</p>
          </div>
          <div className="flex flex-wrap gap-3">
             {user?.role === 'Super-Admin' && (
              <button onClick={() => navigate('/super-admin')} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md shadow-sm hover:bg-gray-50 text-sm font-medium">
                Super Admin
              </button>
            )}
            <button onClick={() => setShowProfile(!showProfile)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md shadow-sm hover:bg-gray-50 text-sm font-medium">
              {showProfile ? 'Hide Profile' : 'Profile'}
            </button>
            <Link to="/admin/add-product" className="bg-primary text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-800 text-sm font-medium">
              + Add Product
            </Link>
          </div>
        </div>

        {/* Profile Section */}
        {showProfile && (
          <div className="mb-8 bg-white shadow rounded-lg border border-gray-200 p-6 animate-fade-in">
             <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Admin Profile</h3>
                <button onClick={handleLogout} className="text-red-600 hover:text-red-800 text-sm font-medium">Sign Out</button>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-500">Name</label>
                    <p className="mt-1 text-gray-900">{user?.name}</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-gray-900">{user?.email}</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-500">Role</label>
                    <p className="mt-1 text-gray-900">{user?.role}</p>
                </div>
             </div>
          </div>
        )}

        {/* Stats Cards - Interactive */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div 
            onClick={() => { setActiveView('orders'); setOrderFilter('pending'); setSearchTerm(''); }}
            className={`bg-white p-6 rounded-lg shadow-sm border cursor-pointer transition-all duration-200 ${activeView === 'orders' && orderFilter === 'pending' ? 'border-accent ring-2 ring-accent ring-opacity-50' : 'border-gray-200 hover:border-accent'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Orders</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{pendingOrdersCount}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full">
                <ShoppingBagIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => { setActiveView('inventory'); setInventoryFilter('low'); }}
            className={`bg-white p-6 rounded-lg shadow-sm border cursor-pointer transition-all duration-200 ${activeView === 'inventory' && inventoryFilter === 'low' ? 'border-red-500 ring-2 ring-red-500 ring-opacity-50' : 'border-gray-200 hover:border-red-300'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Low Stock Items</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{lowStockItems.length}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-full">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => { setActiveView('orders'); setOrderFilter('dispatched'); setSearchTerm(''); }}
            className={`bg-white p-6 rounded-lg shadow-sm border cursor-pointer transition-all duration-200 ${activeView === 'orders' && orderFilter === 'dispatched' ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50' : 'border-gray-200 hover:border-blue-300'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Dispatched Today</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{dispatchedTodayCount}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-full">
                <TruckIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => setActiveView('messages')}
            className={`bg-white p-6 rounded-lg shadow-sm border cursor-pointer transition-all duration-200 ${activeView === 'messages' ? 'border-purple-500 ring-2 ring-purple-500 ring-opacity-50' : 'border-gray-200 hover:border-purple-300'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Support Messages</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{newMessagesCount}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-full">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          {/* Toolbar */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex space-x-4">
              <button 
                onClick={() => { setActiveView('orders'); setOrderFilter('all'); setSearchTerm(''); }}
                className={`px-4 py-2 text-sm font-medium rounded-md ${activeView === 'orders' && orderFilter === 'all' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                All Orders
              </button>
              <button 
                onClick={() => { setActiveView('inventory'); setInventoryFilter('all'); }}
                className={`px-4 py-2 text-sm font-medium rounded-md ${activeView === 'inventory' && inventoryFilter === 'all' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Full Inventory
              </button>
              <button 
                onClick={() => setActiveView('messages')}
                className={`px-4 py-2 text-sm font-medium rounded-md ${activeView === 'messages' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Messages
              </button>
            </div>

            {activeView === 'orders' && (
              <div className="relative w-full sm:w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search Order ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            )}
          </div>

          {/* Table Content */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading data...</div>
            ) : activeView === 'orders' ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.length === 0 ? (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">No orders found.</td></tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">{order.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.customer?.name || 'Guest'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.date}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={order.items}>{order.items}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">₦{order.total.toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' : 
                              order.status === 'Dispatched' ? 'bg-blue-100 text-blue-800' : 
                              order.status === 'Cancelled' ? 'bg-red-100 text-red-800' : 
                              'bg-yellow-100 text-yellow-800'}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <select 
                            value={order.status}
                            onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                            disabled={order.status === 'Delivered' || order.status === 'Cancelled'}
                            className="block w-full pl-3 pr-10 py-1 text-xs border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-xs rounded-md"
                          >
                            <option value="Pending">Pending</option>
                            <option value="Processing">Processing</option>
                            <option value="Dispatched">Dispatched</option>
                            <option value="Delivered">Delivered</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : activeView === 'inventory' ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">No products found.</td></tr>
                  ) : (
                    filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₦{product.price.toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${(product.stock || 0) < 10 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                            {product.stock} {product.unit}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {user?.role === 'Super-Admin' && (
                            <>
                              <Link to={`/admin/edit-product/${product.id}`} className="text-blue-600 hover:text-blue-900 mr-3">Edit</Link>
                              <button onClick={() => handleDeleteProduct(product.id as string)} className="text-red-600 hover:text-red-900">Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {messages.length === 0 ? (
                    <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-500">No messages found.</td></tr>
                  ) : (
                    messages.map((msg) => (
                      <tr key={msg.id} className={`hover:bg-gray-50 ${msg.status === 'New' ? 'bg-purple-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${msg.status === 'New' ? 'bg-purple-100 text-purple-800' : 
                              msg.status === 'Replied' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {msg.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {msg.name}
                          <div className="text-xs text-gray-500">{msg.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{msg.subject}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={msg.message}>{msg.message}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{msg.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2 mb-2">
                            <a href={`mailto:${msg.email}?subject=Re: ${msg.subject}`} className="text-blue-600 hover:text-blue-900" title="Reply via Email"><EnvelopeIcon className="h-5 w-5" /></a>
                            {msg.status === 'New' && (
                              <button onClick={() => handleMarkAsRead(msg.id)} className="text-green-600 hover:text-green-900" title="Mark as Read"><CheckCircleIcon className="h-5 w-5" /></button>
                            )}
                            <button onClick={() => handleDeleteMessage(msg.id)} className="text-red-600 hover:text-red-900" title="Delete"><TrashIcon className="h-5 w-5" /></button>
                          </div>
                          {msg.status === 'New' && (
                            <div className="flex flex-col gap-2 mt-2">
                              <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder="Type reply..."
                                className="w-full p-2 text-xs border border-gray-300 rounded focus:ring-primary focus:border-primary"
                                rows={2}
                              />
                              <button onClick={() => handleSendMessage(msg.id)} className="self-end bg-primary text-white text-xs px-3 py-1 rounded hover:bg-green-700">Send</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
